import {
  CheckOutlined,
  CloseOutlined,
  RetweetOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Claim } from "@rumble-it/common-lib/types/models/Claim.interface";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card } from "@rumbledirect/ui";
import { message, notification, Space } from "antd";
import {
  approveCalculationById,
  approveExpertiseById,
  declineCalculationById,
  requestExpertiseForCalculationById,
  requestReapplyForCalculationById,
} from "../../../../lib/api/resources/claims";
import { Calculation } from "@rumble-it/common-lib/types/models/Calculation.interface";

export const ClaimDossierCalculationActionsCard = ({
  claim,
  calculation,
  setActiveTab,
}: {
  claim: Claim | null | undefined;
  calculation: Calculation | null | undefined;
  setActiveTab: (tab: string) => void;
}) => {
  if (
    claim?.status === "calculatie_goedgekeurd" ||
    claim?.status === "calculatie_afgekeurd"
  )
    return null;

  if (
    claim?.status === "calculatie_expertise_ontvangen" &&
    calculation?.calculationIntent !== "Expertise"
  )
    return null;

  const queryClient = useQueryClient();

  const approveCalculationMutation = useMutation({
    mutationFn: async (calculationId: string) =>
      approveCalculationById(calculationId),
    onSuccess: () => {
      notification.success({
        message: "Calculatie goedgekeurd",
        description:
          "Er is een ESA-40-P bericht verzonden naar de hersteller. De hersteller kan nu de werkzaamheden inplannen.",
      });
      setTimeout(() => {
        queryClient.resetQueries();
        setActiveTab("overview");
      }, 2500);
    },
    onError: () => {
      message.error(
        "Er is iets misgegaan bij het goedkeuren van de calculatie",
      );
    },
  });

  const declineCalculationMutation = useMutation({
    mutationFn: async (calculationId: string) =>
      declineCalculationById(calculationId),
    onSuccess: () => {
      notification.success({
        message: "Calculatie afgekeurd",
        description:
          "Er is een ESA-40-N bericht verzonden naar de hersteller. De calculatie is afgekeurd en de hersteller zal geen werkzaamheden inplannen.",
      });
      setTimeout(() => {
        queryClient.resetQueries();
        setActiveTab("overview");
      }, 2500);
    },
    onError: () => {
      message.error("Er is iets misgegaan bij het afkeuren van de calculatie");
    },
  });

  const requestReapplyCalculationMutation = useMutation({
    mutationFn: async (calculationId: string) =>
      requestReapplyForCalculationById(calculationId),
    onSuccess: () => {
      notification.success({
        message: "Hercalculatie aangevraagd",
        description:
          "Er is een ESA-40-W bericht verzonden naar de hersteller. De hersteller zal een hercalculatie indienen.",
      });
      setTimeout(() => {
        queryClient.resetQueries();
        setActiveTab("overview");
      }, 2500);
    },
    onError: () => {
      message.error(
        "Er is iets misgegaan bij het aanvragen van de hercalculatie",
      );
    },
  });

  const requestExpertiseForCalculationMutation = useMutation({
    mutationFn: async (calculationId: string) =>
      requestExpertiseForCalculationById(calculationId),
    onSuccess: () => {
      notification.success({
        message: "Expertise aangevraagd",
        description:
          "Er is een verzoek tot expertise ingediend. De expert zal spoedig contact opnemen met de betrokken partijen.",
      });
      setTimeout(() => {
        queryClient.resetQueries();
        setActiveTab("overview");
      }, 2500);
    },
    onError: () => {
      message.error("Er is iets misgegaan bij het aanvragen van de expertise");
    },
  });

  const approveExpertiseMutation = useMutation({
    mutationFn: async (calculationId: string) =>
      approveExpertiseById(calculationId),
    onSuccess: () => {
      notification.success({
        message: "Expertise goedgekeurd",
        description:
          "Het expertise rapport is goedgekeurd en de hersteller kan de werkzaamheden inplannen.",
      });
      setTimeout(() => {
        queryClient.resetQueries();
        setActiveTab("overview");
      }, 2500);
    },
    onError: () => {
      message.error("Er is iets misgegaan bij het goedkeuren van de expertise");
    },
  });

  return (
    <Card className="w-full mb-4" title="Acties">
      {claim && calculation?.id && (
        <Space direction="vertical" className="w-full">
          {claim?.status === "calculatie_ontvangen" && (
            <Button
              type="primary"
              size="large"
              className="w-full"
              loading={approveCalculationMutation.isPending}
              onClick={() => {
                if (calculation?.id) {
                  approveCalculationMutation.mutate(calculation.id);
                }
              }}
              icon={<CheckOutlined className="!text-[17px]" />}
            >
              Calculatie goedkeuren
            </Button>
          )}
          {claim?.status === "calculatie_ontvangen" && (
            <Button
              size="large"
              variant="solid"
              className="w-full"
              loading={declineCalculationMutation.isPending}
              onClick={() => {
                if (calculation?.id) {
                  declineCalculationMutation.mutate(calculation.id);
                }
              }}
              icon={<CloseOutlined className="!text-[17px]" />}
            >
              Calculatie afkeuren
            </Button>
          )}
          {claim?.status === "calculatie_ontvangen" && (
            <Button
              size="large"
              className="w-full"
              loading={requestReapplyCalculationMutation.isPending}
              onClick={() => {
                if (calculation?.id) {
                  requestReapplyCalculationMutation.mutate(calculation.id);
                }
              }}
              icon={<RetweetOutlined className="!text-[17px]" />}
            >
              Hercalculatie aanvragen
            </Button>
          )}
          {claim?.status === "calculatie_ontvangen" && (
            <Button
              size="large"
              className="w-full"
              loading={requestExpertiseForCalculationMutation.isPending}
              onClick={() => {
                if (calculation?.id) {
                  requestExpertiseForCalculationMutation.mutate(calculation.id);
                }
              }}
              icon={<UserOutlined className="!text-[17px]" />}
            >
              Expertise aanvragen
            </Button>
          )}
          {claim?.status === "calculatie_expertise_ontvangen" && (
            <Button
              type="primary"
              size="large"
              className="w-full"
              loading={approveExpertiseMutation.isPending}
              onClick={() => {
                if (calculation?.id) {
                  approveExpertiseMutation.mutate(calculation.id);
                }
              }}
              icon={<CheckOutlined className="!text-[17px]" />}
            >
              Expertise goedkeuren
            </Button>
          )}
          {claim?.status === "calculatie_expertise_ontvangen" && (
            <Button
              size="large"
              variant="solid"
              className="w-full"
              icon={<CloseOutlined className="!text-[17px]" />}
            >
              Expertise afkeuren
            </Button>
          )}
        </Space>
      )}
    </Card>
  );
};
